/**
 * The "postcode city" line of the company block on the card.
 *
 * Continental Europe writes the postcode first ("51674 Wiehl"); the UK,
 * Ireland, North America and Oceania write the town first ("Altrincham
 * WA14 5GL"), which is also how the card template on p.47 of the Brand
 * Guidelines 3.0 shows it. Entra's `country` is free text, so the common
 * spellings and ISO codes are all matched.
 *
 * The United States, Canada and Australia also write the state or province
 * between city and postcode. The US and Canada put a comma after the city
 * ("LaGrange, GA 30240", "Toronto, ON M5V 2T6"); Australia does not
 * ("Sydney NSW 2000"). The backend (`card_region` in vcard.py) only sends `state` for
 * those countries and already normalises it to the postal abbreviation
 * ("Georgia" and "Ga" arrive as "GA"); the upper-casing below only guards
 * short codes. Every other country keeps its line without the region: in
 * Entra that field holds counties ("Cheshire"), the country again
 * ("Germany") or Bundesländer, none of which a postal address there needs.
 */
const CITY_FIRST = new Set([
  "united kingdom",
  "uk",
  "gb",
  "great britain",
  "england",
  "scotland",
  "wales",
  "northern ireland",
  "ireland",
  "ie",
  "united states",
  "united states of america",
  "usa",
  "us",
  "canada",
  "ca",
  "australia",
  "au",
  "new zealand",
  "nz",
]);

/**
 * Countries whose address line carries the state, and whether a comma
 * follows the city. Keep in step with `_REGION_COUNTRY` in vcard.py.
 */
const REGION_COUNTRIES = new Map<string, { comma: boolean }>([
  ["united states", { comma: true }],
  ["united states of america", { comma: true }],
  ["usa", { comma: true }],
  ["us", { comma: true }],
  ["canada", { comma: true }],
  ["ca", { comma: true }],
  ["australia", { comma: false }],
  ["au", { comma: false }],
]);

interface CityLineParts {
  postal_code?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
}

/**
 * "51674 Wiehl", "Altrincham WA14 5GL", "LaGrange, GA 30240" or "Sydney
 * NSW 2000"; "" when city, region and postcode are all empty.
 */
export function formatCityLine({ postal_code, city, state, country }: CityLineParts): string {
  const countryKey = (country ?? "").trim().toLowerCase();
  const regionRule = REGION_COUNTRIES.get(countryKey);
  const region = regionRule ? formatRegion(state) : "";
  if (regionRule && region) {
    const cityPart = city?.trim() ?? "";
    const tail = join([region, postal_code]);
    return cityPart && regionRule.comma ? `${cityPart}, ${tail}` : join([cityPart, tail]);
  }
  return join(CITY_FIRST.has(countryKey) ? [city, postal_code] : [postal_code, city]);
}

/** A region code like "ga" becomes "GA"; a longer name is left as written. */
function formatRegion(state: string | null | undefined): string {
  const value = (state ?? "").trim();
  return value.length <= 3 ? value.toUpperCase() : value;
}

function join(parts: Array<string | null | undefined>): string {
  return parts
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
}
